import { eventReturnTypeAlias } from '@modules/utilities/pageBuild/getReferencePageTypesData';
import React, { FunctionComponent, createContext, useContext } from 'react';
import getCleanType from '@modules/utilities/pageBuild/getCleanType';
import {
  NextjsSerializableReferenceTypeDataByTypeAlias,
  HybridTypeAliasDatum,
  TypeAliasDatumItem,
  MultiOrTypesCompoundTypeAliasDatum,
} from '../../types';
import { parseAliasForCompoundTypes } from '../utils/linkableTypeReferenceUtils';

export type ReferenceTypesDataProviderProps = {
  referenceTypesData: NextjsSerializableReferenceTypeDataByTypeAlias;
};

export type ReferenceTypesDataContext = {
  eventReturnTypeAliasDatum: TypeAliasDatumItem; // defined on provider mount
  getTypeAliasDatumItem: (yamlTypeString: string) => TypeAliasDatumItem;
  getTypeAliasDatum: (
    yamlTypeString: string,
  ) => HybridTypeAliasDatum | MultiOrTypesCompoundTypeAliasDatum;
};

const MISSING_EVENT_TYPE_CANONICAL_LABEL = 'MISSING_EVENT_TYPE';

const ReferenceTypesDataContext = createContext<ReferenceTypesDataContext>({
  eventReturnTypeAliasDatum: {
    canonicalLabel: MISSING_EVENT_TYPE_CANONICAL_LABEL,
  },
  getTypeAliasDatumItem: () => {
    throw new Error('getTypeAliasDatumItem not implemented');
  },
  getTypeAliasDatum: () => {
    throw new Error('getTypeAliasDatum not implemented');
  },
});
ReferenceTypesDataContext.displayName = 'ReferenceTypesData';

export function useReferenceTypesData() {
  return useContext(ReferenceTypesDataContext);
}

function assertIsReferenceTypesDataContext(
  contextVal: ReferenceTypesDataProviderProps | ReferenceTypesDataContext,
): asserts contextVal is ReferenceTypesDataContext {
  if (
    !(
      !!contextVal &&
      typeof contextVal === 'object' &&
      'eventReturnTypeAliasDatum' in contextVal &&
      'getTypeAliasDatumItem' in contextVal &&
      'getTypeAliasDatum' in contextVal
    ) ||
    contextVal.eventReturnTypeAliasDatum.canonicalLabel === MISSING_EVENT_TYPE_CANONICAL_LABEL
  ) {
    throw new Error(
      `Invalid context received for ReferenceTypesDataContext. Got ${JSON.stringify(contextVal)}`,
    );
  }
}

const getTypeAliasDatumItemFromRefTypes = (
  referenceTypesData: NextjsSerializableReferenceTypeDataByTypeAlias,
  singleType: string,
): TypeAliasDatumItem => {
  const cleanedType = getCleanType(singleType);
  const datumItem = referenceTypesData[cleanedType];
  if (!datumItem) throw new Error(`TypeAliasDatum not found for type: ${cleanedType}`);
  return datumItem;
};

const getHybridTypeAliasDatum = (
  referenceTypesData: NextjsSerializableReferenceTypeDataByTypeAlias,
  outerType: string,
  innerTypes: string[],
): HybridTypeAliasDatum => {
  const outerTypeAliasDatumItem = getTypeAliasDatumItemFromRefTypes(referenceTypesData, outerType);
  const innerTypeAliasDatumItems = innerTypes.map((innerType: string) =>
    getTypeAliasDatumItemFromRefTypes(referenceTypesData, innerType),
  );
  if (!outerTypeAliasDatumItem) throw new Error(`TypeAliasDatum not found for type: ${outerType}`);
  if (innerTypeAliasDatumItems.some((item) => !item)) {
    throw new Error(`TypeAliasDatumItem not found among types: ${innerTypes}`);
  }
  return {
    outerType: outerTypeAliasDatumItem,
    innerTypes: innerTypeAliasDatumItems,
  };
};
const getMultiOrTypesCompoundTypeAliasDatum = (
  referenceTypesData: NextjsSerializableReferenceTypeDataByTypeAlias,
  multipleOrTypes: string[],
): MultiOrTypesCompoundTypeAliasDatum => {
  return {
    multipleOrTypes: multipleOrTypes.map((typeOption: string) => {
      const datumItem = getTypeAliasDatumItemFromRefTypes(referenceTypesData, typeOption);
      if (!datumItem) throw new Error(`TypeAliasDatum not found for type: ${typeOption}`);
      return datumItem;
    }),
  };
};

const ReferenceTypesDataProvider: FunctionComponent<
  React.PropsWithChildren<ReferenceTypesDataProviderProps>
> = ({
  referenceTypesData, // TypeAliasDatum's by page typeAlias
  children,
}) => {
  const eventReturnTypeAliasDatum = getTypeAliasDatumItemFromRefTypes(
    referenceTypesData,
    eventReturnTypeAlias,
  );
  const getTypeAliasDatum = (
    type: string,
  ): HybridTypeAliasDatum | MultiOrTypesCompoundTypeAliasDatum => {
    // convert to TypeAliasDatum object from TypeAliasDatumItem's in
    // referenceTypesData
    const { outerType, innerTypes, multipleOrTypes } = parseAliasForCompoundTypes({
      type,
    });
    if (outerType && innerTypes) {
      return getHybridTypeAliasDatum(referenceTypesData, outerType, innerTypes);
    }
    if (multipleOrTypes) {
      return getMultiOrTypesCompoundTypeAliasDatum(referenceTypesData, multipleOrTypes);
    }
    throw new Error(`Cannot parse for compound types from type: ${type}`);
  };
  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const contextVal = {
    referenceTypesData,
    getTypeAliasDatumItem: (type: string) =>
      getTypeAliasDatumItemFromRefTypes(referenceTypesData, type),
    getTypeAliasDatum,
    eventReturnTypeAliasDatum,
  };

  assertIsReferenceTypesDataContext(contextVal);

  return (
    <ReferenceTypesDataContext.Provider value={contextVal}>
      {children}
    </ReferenceTypesDataContext.Provider>
  );
};

export default ReferenceTypesDataProvider;
